import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import axios from 'axios';
import { useAuth } from '../App';
import { API } from '../utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Camera, ArrowLeft, Plus, Briefcase, MapPin, DollarSign, 
  Calendar as CalendarIcon, Clock, Users, Send, CheckCircle, Upload, Paperclip, X, Eye, Building2,
  Pause, Play, XCircle, MoreVertical
} from 'lucide-react';

const Projects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(location.state?.openCreateDialog || false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [viewAppOpen, setViewAppOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [specialties, setSpecialties] = useState({ photography: [], videography: [] });
  const [activeTab, setActiveTab] = useState('browse');
  const [initialTabSet, setInitialTabSet] = useState(false);
  
  // Set initial active tab when user role is known (only once)
  useEffect(() => {
    if (!initialTabSet && user?.role) {
      if (user.role === 'business') {
        setActiveTab('my-projects');
      } else if (user.role === 'creative') {
        setActiveTab('browse');
      }
      setInitialTabSet(true);
    }
  }, [user?.role, initialTabSet]);
  
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    deliverables: '',
    specialty_required: [],
    budget_min: '',
    budget_max: '',
    location: '',
    event_date: null,
    event_time: '10:00',
    shoot_duration: '2',
    application_deadline: null,
    deliverables_deadline: null
  });
  
  const [application, setApplication] = useState({
    cover_letter: '',
    proposed_rate: '',
    proposed_revisions: 2,
    questions: '',
    attachment_url: ''
  });
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentFilename, setAttachmentFilename] = useState('');
  const [myPackages, setMyPackages] = useState([]);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const config = { withCredentials: true, headers };

  useEffect(() => {
    fetchSpecialties();
    fetchProjects();
    if (user?.role === 'business') {
      fetchMyProjects();
    } else if (user?.role === 'creative') {
      fetchMyApplications();
      fetchMyPackages();
    }
  }, [user]);

  const fetchMyPackages = async () => {
    try {
      const response = await axios.get(`${API}/service-packages/my`, config);
      setMyPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const selectPackageForApplication = (pkg) => {
    setApplication({
      ...application,
      proposed_rate: pkg.price,
      proposed_revisions: pkg.revisions_included,
      cover_letter: application.cover_letter || `I'd like to offer my "${pkg.name}" package for this project.\n\nThis includes: ${pkg.deliverables}\n\nDuration: ${pkg.duration_hours} hours\nRevisions: ${pkg.revisions_included}`
    });
  };

  const fetchSpecialties = async () => {
    try {
      const response = await axios.get(`${API}/specialties`);
      setSpecialties(response.data);
    } catch (error) {
      console.error('Error fetching specialties:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects/my/posted`, config);
      setMyProjects(response.data);
    } catch (error) {
      console.error('Error fetching my projects:', error);
    }
  };

  const fetchMyApplications = async () => {
    try {
      const response = await axios.get(`${API}/projects/my/applications`, config);
      setMyApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.title || !newProject.description || !newProject.application_deadline || !newProject.location) {
      toast.error('Please fill in all required fields (title, description, location, application deadline)');
      return;
    }

    try {
      await axios.post(`${API}/projects`, {
        ...newProject,
        budget_min: parseFloat(newProject.budget_min) || 0,
        budget_max: parseFloat(newProject.budget_max) || 0,
        application_deadline: format(newProject.application_deadline, 'yyyy-MM-dd'),
        deliverables_deadline: newProject.deliverables_deadline ? format(newProject.deliverables_deadline, 'yyyy-MM-dd') : null,
        event_date: newProject.event_date ? format(newProject.event_date, 'yyyy-MM-dd') : null,
        event_time: newProject.event_time,
        shoot_duration: parseFloat(newProject.shoot_duration) || 2,
        deadline: format(newProject.application_deadline, 'yyyy-MM-dd') // Legacy field
      }, config);

      toast.success('Project posted successfully!');
      setCreateOpen(false);
      setNewProject({ title: '', description: '', deliverables: '', specialty_required: [], budget_min: '', budget_max: '', location: '', event_date: null, event_time: '10:00', shoot_duration: '2', application_deadline: null, deliverables_deadline: null });
      fetchProjects();
      fetchMyProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post project');
    }
  };

  const uploadAttachmentFile = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 50MB');
      return;
    }
    setAttachmentUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload/application-attachment`, formData, {
        ...config,
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' }
      });
      setApplication(prev => ({ ...prev, attachment_url: response.data.url }));
      setAttachmentFilename(file.name);
      toast.success('Attachment uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload attachment');
    } finally {
      setAttachmentUploading(false);
    }
  };

  const handleAttachmentUpload = (e) => uploadAttachmentFile(e.target.files?.[0]);

  const handleAttachmentDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) uploadAttachmentFile(file);
  };

  const removeAttachment = () => {
    setApplication({ ...application, attachment_url: '' });
    setAttachmentFilename('');
  };

  const handleApply = async () => {
    if (!application.cover_letter || !application.proposed_rate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/projects/${selectedProject.project_id}/apply`, {
        project_id: selectedProject.project_id,
        cover_letter: application.cover_letter,
        proposed_rate: parseFloat(application.proposed_rate),
        proposed_revisions: parseInt(application.proposed_revisions) || 2,
        questions: application.questions || null,
        attachment_url: application.attachment_url || null
      }, config);

      toast.success('Application submitted!');
      setApplyOpen(false);
      setApplication({ cover_letter: '', proposed_rate: '', proposed_revisions: 2, questions: '', attachment_url: '' });
      setAttachmentFilename('');
      await fetchMyApplications();
      setActiveTab('applications'); // Switch to My Applications tab
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply');
    }
  };

  const allSpecialties = [...specialties.photography, ...specialties.videography];

  // Project management handlers (for businesses)
  const handlePauseProject = async (projectId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/pause`, {}, config);
      toast.success('Project paused - hidden from new applicants');
      fetchMyProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause project');
    }
  };

  const handleResumeProject = async (projectId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/resume`, {}, config);
      toast.success('Project resumed - visible to applicants again');
      fetchMyProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume project');
    }
  };

  const handleCancelProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to cancel this project? All pending applications will be rejected.')) {
      return;
    }
    try {
      const response = await axios.post(`${API}/projects/${projectId}/cancel`, {}, config);
      toast.success(`Project cancelled. ${response.data.applications_rejected} applications rejected.`);
      fetchMyProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel project');
    }
  };

  // Application withdrawal (for creatives)
  const handleWithdrawApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to withdraw this application?')) {
      return;
    }
    try {
      await axios.post(`${API}/applications/${applicationId}/withdraw`, {}, config);
      toast.success('Application withdrawn');
      fetchMyApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw application');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-100 text-green-700';
      case 'paused': return 'bg-yellow-100 text-yellow-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-[#f7f5f2] text-[#0e0e0e]';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'withdrawn': return 'bg-[#f7f5f2] text-[#0e0e0e]';
      default: return 'bg-[#f7f5f2] text-[#0e0e0e]';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-10">
        <div className="animate-pulse text-[#a0a0a0] text-sm">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 overflow-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-[#a0a0a0] uppercase tracking-wider">Marketplace</p>
            <h1 className="text-3xl font-display font-normal tracking-tight">Projects</h1>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === 'business' && (
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full px-6 bg-[#0e0e0e] text-white hover:bg-[#1a1a1a]" data-testid="post-project-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Post Project
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold">Post a Project</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Project Title *</Label>
                      <Input
                        value={newProject.title}
                        onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                        placeholder="e.g., Product Photos for E-commerce"
                        className="mt-1 border-[#e4e1db] focus:border-[#0e0e0e] focus:ring-[#0e0e0e]"
                        data-testid="project-title-input"
                      />
                    </div>
                    <div>
                      <Label>Description *</Label>
                      <Textarea
                        value={newProject.description}
                        onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                        placeholder="Describe what you need in detail..."
                        className="mt-1 border-[#e4e1db] focus:border-[#0e0e0e] focus:ring-[#0e0e0e]"
                        rows={3}
                        data-testid="project-description-input"
                      />
                    </div>
                    <div>
                      <Label>Expected Deliverables</Label>
                        <Textarea
                          value={newProject.deliverables}
                          onChange={(e) => setNewProject({ ...newProject, deliverables: e.target.value })}
                          placeholder="e.g., 20 edited photos, 1 video highlight reel, raw files..."
                          className="mt-1 border-[#e4e1db] focus:border-[#0e0e0e] focus:ring-[#0e0e0e]"
                          rows={2}
                          data-testid="project-deliverables-input"
                        />
                      </div>
                      <div>
                        <Label>Location *</Label>
                        <Input
                          value={newProject.location}
                          onChange={(e) => setNewProject({ ...newProject, location: e.target.value })}
                          placeholder="e.g., Los Angeles, CA or Remote"
                          className="mt-1 border-[#e4e1db] focus:border-[#0e0e0e] focus:ring-[#0e0e0e]"
                          data-testid="project-location-input"
                        />
                      </div>
                      <div>
                        <Label>Specialties Needed</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {allSpecialties.slice(0, 8).map((s) => (
                            <button
                              key={s}
                              type="button"
                              onClick={() => {
                                const specs = newProject.specialty_required.includes(s)
                                  ? newProject.specialty_required.filter(x => x !== s)
                                  : [...newProject.specialty_required, s];
                                setNewProject({ ...newProject, specialty_required: specs });
                              }}
                              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                                newProject.specialty_required.includes(s)
                                  ? 'bg-[#0e0e0e] text-white'
                                  : 'bg-[#f7f5f2] text-[#0e0e0e] hover:bg-[#e4e1db]'
                              }`}
                            >
                              {s}
                            </button>
                          ))}
                          {newProject.specialty_required.filter(s => !allSpecialties.includes(s)).map((s) => (
                            <button
                              key={s}
                              type="button"
                              onClick={() => setNewProject({ ...newProject, specialty_required: newProject.specialty_required.filter(x => x !== s) })}
                              className="px-3 py-1 rounded-full text-sm bg-[#0e0e0e] text-white flex items-center gap-1"
                            >
                              {s} <span className="text-xs">×</span>
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2 mt-2">
                          <input
                            type="text"
                            placeholder="Add custom specialty..."
                            className="border border-[#e4e1db] rounded-full px-3 py-1 text-sm focus:outline-none focus:border-[#0e0e0e] flex-1"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const val = e.target.value.trim();
                                if (val && !newProject.specialty_required.includes(val)) {
                                  setNewProject({ ...newProject, specialty_required: [...newProject.specialty_required, val] });
                                }
                                e.target.value = '';
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Budget Min ($)</Label>
                          <Input
                            type="number"
                            value={newProject.budget_min}
                            onChange={(e) => setNewProject({ ...newProject, budget_min: e.target.value })}
                            placeholder="500"
                            className="mt-1 border-[#e4e1db] focus:border-[#0e0e0e] focus:ring-[#0e0e0e]"
                          />
                        </div>
                        <div>
                          <Label>Budget Max ($)</Label>
                          <Input
                            type="number"
                            value={newProject.budget_max}
                            onChange={(e) => setNewProject({ ...newProject, budget_max: e.target.value })}
                            placeholder="1500"
                            className="mt-1 border-[#e4e1db] focus:border-[#0e0e0e] focus:ring-[#0e0e0e]"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-[#f7f5f2] rounded-lg">
                        <Label className="text-sm font-medium">Shoot/Event Details</Label>
                        <div className="grid grid-cols-3 gap-4 mt-2">
                          <div>
                            <Label className="text-xs text-[#5a5a5a]">Shoot Date</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start mt-1 border-[#e4e1db] text-sm">
                                  <CalendarIcon className="w-4 h-4 mr-2" />
                                  {newProject.event_date ? format(newProject.event_date, 'MMM d') : 'Date'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0">
                                <Calendar
                                  mode="single"
                                  selected={newProject.event_date}
                                  onSelect={(date) => setNewProject({ ...newProject, event_date: date })}
                                  disabled={(date) => date < new Date()}
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <Label className="text-xs text-[#5a5a5a]">Start Time</Label>
                            <Input
                              type="time"
                              value={newProject.event_time}
                              onChange={(e) => setNewProject({ ...newProject, event_time: e.target.value })}
                              className="mt-1 border-[#e4e1db] focus:border-[#0e0e0e] focus:ring-[#0e0e0e]"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#5a5a5a]">Duration (hours)</Label>
                            <Input
                              type="number"
                              min="1"
                              max="24"
                              value={newProject.shoot_duration}
                              onChange={(e) => setNewProject({ ...newProject, shoot_duration: e.target.value })}
                              placeholder="2"
                              className="mt-1 border-[#e4e1db] focus:border-[#0e0e0e] focus:ring-[#0e0e0e]"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Deadlines Section */}
                      <div className="p-3 bg-[#f5ead0] rounded-lg border border-[#e8d49a]">
                        <Label className="text-sm font-medium text-[#8a6a1a]">Important Deadlines</Label>
                        <div className="space-y-3 mt-2">
                          <div>
                            <Label className="text-xs text-[#5a5a5a]">Application Deadline * (when to stop accepting applications)</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start mt-1 border-[#e4e1db]">
                                  <CalendarIcon className="w-4 h-4 mr-2" />
                                  {newProject.application_deadline ? format(newProject.application_deadline, 'PPP') : 'Select deadline'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0">
                                <Calendar
                                  mode="single"
                                  selected={newProject.application_deadline}
                                  onSelect={(date) => setNewProject({ ...newProject, application_deadline: date })}
                                  disabled={(date) => date < new Date()}
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <Label className="text-xs text-[#5a5a5a]">Deliverables Deadline (when final edited files are due)</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start mt-1 border-[#e4e1db]">
                                  <CalendarIcon className="w-4 h-4 mr-2" />
                                  {newProject.deliverables_deadline ? format(newProject.deliverables_deadline, 'PPP') : 'Select deadline'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0">
                                <Calendar
                                  mode="single"
                                  selected={newProject.deliverables_deadline}
                                  onSelect={(date) => setNewProject({ ...newProject, deliverables_deadline: date })}
                                  disabled={(date) => date < new Date()}
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        onClick={handleCreateProject}
                        className="w-full rounded-full bg-[#0e0e0e] text-white hover:bg-[#1a1a1a]"
                        data-testid="submit-project-btn"
                      >
                        Post Project
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-6">
            {user?.role === 'creative' && (
              <button
                onClick={() => setActiveTab('browse')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === 'browse' ? 'bg-[#0e0e0e] text-white' : 'bg-[#f7f5f2] text-[#0e0e0e] hover:bg-[#e4e1db]'
                }`}
              >
                Browse Projects
              </button>
            )}
            {user?.role === 'business' && (
              <button
                onClick={() => setActiveTab('my-projects')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === 'my-projects' ? 'bg-[#0e0e0e] text-white' : 'bg-[#f7f5f2] text-[#0e0e0e] hover:bg-[#e4e1db]'
                }`}
              >
                My Projects ({myProjects.length})
              </button>
            )}
            {user?.role === 'creative' && (
              <button
                onClick={() => setActiveTab('applications')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === 'applications' ? 'bg-[#0e0e0e] text-white' : 'bg-[#f7f5f2] text-[#0e0e0e] hover:bg-[#e4e1db]'
                }`}
              >
                My Applications ({myApplications.length})
              </button>
            )}
            {!user && (
              <button
                onClick={() => setActiveTab('browse')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === 'browse' ? 'bg-[#0e0e0e] text-white' : 'bg-[#f7f5f2] text-[#0e0e0e] hover:bg-[#e4e1db]'
                }`}
              >
                Browse Projects
              </button>
            )}
          </div>
        </div>

      {/* Content */}
      <div className="mt-6">
        {(activeTab === 'browse' && user?.role !== 'business') && (
          <div className="space-y-4">
            {projects.length > 0 ? (
              projects.map((project) => (
                <div
                  key={project.project_id}
                  className="bg-white rounded-[14px] p-6 border border-[#e4e1db] hover:shadow-md transition-shadow"
                  data-testid={`project-${project.project_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{project.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                          {project.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-[#5a5a5a] mb-4 line-clamp-2">{project.description}</p>
                      
                      {/* Project deliverables if any */}
                      {project.deliverables && (
                        <div className="mb-3 p-2 bg-[#f7f5f2] rounded-lg">
                          <p className="text-xs font-medium text-[#5a5a5a] mb-1">Expected Deliverables:</p>
                          <p className="text-sm text-[#0e0e0e]">{project.deliverables}</p>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-[#5a5a5a] mb-4">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          ${project.budget_min} - ${project.budget_max}
                        </div>
                        {project.location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {project.location}
                          </div>
                        )}
                        {/* Event Date/Time - important for creatives */}
                        {project.event_date && (
                          <div className="flex items-center gap-1 text-[#d4a843] font-medium">
                            <CalendarIcon className="w-4 h-4" />
                            Shoot: {project.event_date} {project.event_time && `@ ${project.event_time}`}
                            {project.shoot_duration && ` (${project.shoot_duration}h)`}
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Apply by: {project.application_deadline || project.deadline}
                        </div>
                        {project.deliverables_deadline && (
                          <div className="flex items-center gap-1 text-[#d4a843]">
                            <CheckCircle className="w-4 h-4" />
                            Finals due: {project.deliverables_deadline}
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {project.application_count} applications
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {project.specialty_required?.map((s, idx) => (
                          <span key={idx} className="px-3 py-1 rounded-full bg-[#f7f5f2] text-[#0e0e0e] text-xs">
                            {s}
                          </span>
                        ))}
                      </div>

                      <p className="text-sm mt-4">Posted by <span className="font-medium">{project.business_name}</span></p>
                    </div>

                    <div className="flex flex-col gap-2">
                      {user?.role === 'creative' && (
                        <Button
                          variant="outline"
                          onClick={() => { setSelectedProject(project); setDetailsOpen(true); }}
                          className="rounded-full px-6 border-[#e4e1db] hover:border-[#0e0e0e]"
                          data-testid="view-details-btn"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </Button>
                      )}
                      {user?.role === 'creative' && project.status === 'open' && (
                        <Button
                          onClick={() => { setSelectedProject(project); setApplyOpen(true); }}
                          className="rounded-full px-6 bg-[#0e0e0e] text-white hover:bg-[#1a1a1a]"
                          data-testid="apply-btn"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Apply
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-16 bg-white rounded-[14px] border border-[#e4e1db]">
                <Briefcase className="w-16 h-16 mx-auto text-gray-200 mb-4" />
                <h3 className="text-2xl font-bold mb-2">No projects yet</h3>
                <p className="text-[#5a5a5a]">Check back later for new opportunities</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'my-projects' && user?.role === 'business' && (
          <div className="space-y-4">
            {myProjects.length > 0 ? (
              myProjects.map((project) => (
                <div
                  key={project.project_id}
                  className="bg-white rounded-[14px] p-6 border border-[#e4e1db]"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{project.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                          {project.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-muted-foreground text-sm">
                        {project.application_count} applications • Due: {project.deadline}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {project.status === 'open' && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => { e.preventDefault(); handlePauseProject(project.project_id); }}
                            title="Pause project"
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={(e) => { e.preventDefault(); handleCancelProject(project.project_id); }}
                            title="Cancel project"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {project.status === 'paused' && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => { e.preventDefault(); handleResumeProject(project.project_id); }}
                            title="Resume project"
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={(e) => { e.preventDefault(); handleCancelProject(project.project_id); }}
                            title="Cancel project"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Link to={`/projects/${project.project_id}/applications`}>
                        <Button variant="outline" className="rounded-full">
                          View Applications
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl">
                <Briefcase className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-heading text-2xl font-semibold mb-2">No projects posted</h3>
                <p className="text-muted-foreground mb-6">Post your first project to find creatives</p>
                <Button onClick={() => setCreateOpen(true)} className="rounded-full px-6 bg-primary hover:bg-primary/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Post Project
                </Button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'applications' && user?.role === 'creative' && (
          <div className="space-y-4">
            {myApplications.length > 0 ? (
              myApplications.map((app) => (
                <div
                  key={app.application_id}
                  className="bg-white rounded-2xl p-6"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{app.project?.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                          {app.status === 'rejected' ? 'Reviewed' : app.status}
                        </span>
                        {app.attachment_url && (
                          <span className="px-2 py-0.5 rounded-full bg-[#f7f5f2] text-[#0e0e0e] text-xs flex items-center gap-1">
                            <Paperclip className="w-3 h-3" />
                            Attachment
                          </span>
                        )}
                      </div>
                      <p className="text-muted-foreground text-sm">
                        Proposed rate: ${app.proposed_rate} • {app.proposed_revisions || 2} revisions • Applied: {new Date(app.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {app.status === 'accepted' && (
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">Hired!</span>
                        </div>
                      )}
                      {app.status !== 'accepted' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full border-[#e4e1db] hover:border-[#0e0e0e]"
                          onClick={() => { setSelectedApp(app); setViewAppOpen(true); }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Application
                        </Button>
                      )}
                      {app.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleWithdrawApplication(app.application_id)}
                        >
                          Withdraw
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl">
                <Send className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-heading text-2xl font-semibold mb-2">No applications yet</h3>
                <p className="text-muted-foreground mb-6">Browse projects and apply to get started</p>
                <Button onClick={() => setActiveTab('browse')} variant="outline" className="rounded-full">
                  Browse Projects
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Application Dialog */}
      <Dialog open={viewAppOpen} onOpenChange={setViewAppOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Your Application</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-4 mt-4">
              <div className="p-4 rounded-[14px] bg-[#f7f5f2] border border-[#e4e1db] space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold">{selectedApp.project?.title}</h4>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedApp.status)}`}>
                    {selectedApp.status}
                  </span>
                </div>
                <p className="text-xs text-[#5a5a5a]">Applied: {new Date(selectedApp.created_at).toLocaleDateString()}</p>
                {selectedApp.project?.description && (
                  <p className="text-sm text-[#0e0e0e]">{selectedApp.project.description}</p>
                )}
                <div className="flex flex-wrap gap-3 text-sm text-[#5a5a5a] pt-1">
                  {selectedApp.project?.budget_min != null && (
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3.5 h-3.5" />
                      ${selectedApp.project.budget_min} – ${selectedApp.project.budget_max}
                    </span>
                  )}
                  {selectedApp.project?.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {selectedApp.project.location}
                    </span>
                  )}
                  {selectedApp.project?.event_date && (
                    <span className="flex items-center gap-1 text-[#d4a843] font-medium">
                      <CalendarIcon className="w-3.5 h-3.5" />
                      {selectedApp.project.event_date}{selectedApp.project.event_time ? ` @ ${selectedApp.project.event_time}` : ''}
                    </span>
                  )}
                  {selectedApp.project?.application_deadline && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      Deadline: {selectedApp.project.application_deadline}
                    </span>
                  )}
                </div>
                {selectedApp.project?.deliverables && (
                  <div className="pt-1 border-t border-[#e4e1db]">
                    <p className="text-xs font-medium text-[#5a5a5a] mb-0.5">Expected Deliverables</p>
                    <p className="text-sm text-[#0e0e0e]">{selectedApp.project.deliverables}</p>
                  </div>
                )}
                {selectedApp.project?.specialty_required?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {selectedApp.project.specialty_required.map((s, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-[#e4e1db] text-[#0e0e0e] text-xs">{s}</span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#a0a0a0] mb-1">Proposed Rate</p>
                <p className="text-sm">${selectedApp.proposed_rate}</p>
              </div>
              {selectedApp.proposed_revisions != null && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#a0a0a0] mb-1">Revisions</p>
                  <p className="text-sm">{selectedApp.proposed_revisions}</p>
                </div>
              )}
              {selectedApp.cover_letter && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#a0a0a0] mb-1">Cover Letter</p>
                  <p className="text-sm whitespace-pre-wrap">{selectedApp.cover_letter}</p>
                </div>
              )}
              {selectedApp.questions && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#a0a0a0] mb-1">Questions for Client</p>
                  <p className="text-sm whitespace-pre-wrap">{selectedApp.questions}</p>
                </div>
              )}
              {selectedApp.attachment_url && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#a0a0a0] mb-1">Attachment</p>
                  <a href={selectedApp.attachment_url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#d4a843] underline flex items-center gap-1">
                    <Paperclip className="w-3 h-3" /> View Attachment
                  </a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply Dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Apply to Project</DialogTitle>
          </DialogHeader>
          {selectedProject && (
            <div className="space-y-4 mt-4">
              <div className="p-4 rounded-[14px] bg-[#f7f5f2] border border-[#e4e1db]">
                <h4 className="font-semibold">{selectedProject.title}</h4>
                <p className="text-sm text-[#5a5a5a]">Budget: ${selectedProject.budget_min} - ${selectedProject.budget_max}</p>
                {selectedProject.event_date && (
                  <p className="text-sm text-[#d4a843] mt-1">
                    Shoot Date: {selectedProject.event_date} {selectedProject.event_time && `@ ${selectedProject.event_time}`}
                  </p>
                )}
                {selectedProject.deliverables_deadline && (
                  <p className="text-sm text-[#d4a843] mt-1">
                    Finals Due: {selectedProject.deliverables_deadline}
                  </p>
                )}
                {selectedProject.deliverables && (
                  <div className="mt-2 pt-2 border-t border-[#e4e1db]">
                    <p className="text-xs font-medium text-[#5a5a5a]">Expected Deliverables:</p>
                    <p className="text-sm text-[#0e0e0e]">{selectedProject.deliverables}</p>
                  </div>
                )}
              </div>
              
              {/* Quick Package Selection */}
              {myPackages.length > 0 && (
                <div>
                  <Label className="mb-2 block">Quick Apply with Package (optional)</Label>
                  <div className="flex flex-wrap gap-2">
                    {myPackages.map((pkg) => (
                      <button
                        key={pkg.package_id}
                        type="button"
                        onClick={() => selectPackageForApplication(pkg)}
                        className="px-3 py-2 text-sm rounded-lg border border-[#e4e1db] hover:border-[#0e0e0e] hover:bg-[#f7f5f2] transition-colors"
                      >
                        <span className="font-medium">{pkg.name}</span>
                        <span className="text-[#d4a843] ml-2">${pkg.price}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div>
                <Label>Cover Letter *</Label>
                <Textarea
                  value={application.cover_letter}
                  onChange={(e) => setApplication({ ...application, cover_letter: e.target.value })}
                  placeholder="Introduce yourself and explain why you're a good fit..."
                  className="mt-1 border-[#e4e1db] focus:border-[#0e0e0e] focus:ring-[#0e0e0e]"
                  rows={4}
                  data-testid="cover-letter-input"
                />
              </div>
              
              {/* Questions Section - NEW */}
              <div>
                <Label>Questions for the Client (optional)</Label>
                <p className="text-xs text-[#5a5a5a] mb-1">Ask clarifying questions or propose additional deliverables</p>
                <Textarea
                  value={application.questions}
                  onChange={(e) => setApplication({ ...application, questions: e.target.value })}
                  placeholder="e.g., Would you prefer indoor or outdoor shots? I can also provide drone footage for an additional fee..."
                  className="mt-1 border-[#e4e1db] focus:border-[#0e0e0e] focus:ring-[#0e0e0e]"
                  rows={3}
                  data-testid="application-questions-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Your Proposed Rate ($) *</Label>
                  <Input
                    type="number"
                    value={application.proposed_rate}
                    onChange={(e) => setApplication({ ...application, proposed_rate: e.target.value })}
                    placeholder="e.g., 800"
                    className="mt-1 border-[#e4e1db] focus:border-[#0e0e0e] focus:ring-[#0e0e0e]"
                    data-testid="proposed-rate-input"
                  />
                </div>
                <div>
                  <Label>Revisions Included</Label>
                  <Select 
                    value={String(application.proposed_revisions)} 
                    onValueChange={(val) => setApplication({ ...application, proposed_revisions: parseInt(val) })}
                  >
                    <SelectTrigger className="mt-1 border-[#e4e1db]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 revision</SelectItem>
                      <SelectItem value="2">2 revisions</SelectItem>
                      <SelectItem value="3">3 revisions</SelectItem>
                      <SelectItem value="5">5 revisions</SelectItem>
                      <SelectItem value="0">Unlimited</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Attachment Upload Section - NEW */}
              <div>
                <Label>Attach Sample Work (optional)</Label>
                <p className="text-xs text-[#5a5a5a] mb-2">Upload a relevant sample, PDF, or video (max 50MB)</p>
                
                {!application.attachment_url ? (
                  <label
                    className="flex items-center justify-center w-full h-24 border-2 border-dashed border-[#e4e1db] rounded-lg cursor-pointer hover:border-gray-400 transition-colors"
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-gray-400', 'bg-[#f7f5f2]'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-gray-400', 'bg-[#f7f5f2]'); }}
                    onDrop={(e) => { e.currentTarget.classList.remove('border-gray-400', 'bg-[#f7f5f2]'); handleAttachmentDrop(e); }}
                  >
                    <div className="flex flex-col items-center">
                      {attachmentUploading ? (
                        <>
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0e0e0e] mb-2"></div>
                          <span className="text-sm text-[#5a5a5a]">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-[#a0a0a0] mb-2" />
                          <span className="text-sm text-[#5a5a5a]">Click to upload or drag & drop</span>
                          <span className="text-xs text-[#a0a0a0]">JPG, PNG, PDF, MP4 up to 50MB</span>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.mp4,.mov,.webm"
                      onChange={handleAttachmentUpload}
                      disabled={attachmentUploading}
                      data-testid="attachment-upload-input"
                    />
                  </label>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Paperclip className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700 truncate max-w-[200px]">{attachmentFilename}</span>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <button
                      type="button"
                      onClick={removeAttachment}
                      className="p-1 hover:bg-green-100 rounded"
                      data-testid="remove-attachment-btn"
                    >
                      <X className="w-4 h-4 text-green-600" />
                    </button>
                  </div>
                )}
              </div>
              
              <Button
                onClick={handleApply}
                className="w-full rounded-full bg-primary hover:bg-primary/90"
                data-testid="submit-application-btn"
              >
                Submit Application
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Project Details</DialogTitle>
          </DialogHeader>
          {selectedProject && (
            <div className="space-y-6 mt-4">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold">{selectedProject.title}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      selectedProject.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-[#f7f5f2] text-[#0e0e0e]'
                    }`}>
                      {selectedProject.status}
                    </span>
                    <span className="text-sm text-[#5a5a5a]">
                      {selectedProject.application_count} applications
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-semibold text-[#d4a843]">
                    ${selectedProject.budget_min} – ${selectedProject.budget_max}
                  </p>
                  <p className="text-xs text-[#5a5a5a]">Budget Range</p>
                </div>
              </div>

              {/* Posted By */}
              <Link 
                to={`/business/${(selectedProject.business_name || 'business').replace(/\s+/g, '')}_${(selectedProject.business_id || '').replace('user_', '')}`}
                className="flex items-center gap-3 p-4 bg-[#f7f5f2] rounded-[14px] hover:bg-[#eeece8] transition-colors cursor-pointer"
                onClick={() => setDetailsOpen(false)}
              >
                <div className="w-10 h-10 rounded-full bg-[#e4e1db] flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#5a5a5a]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-[#5a5a5a]">Posted by</p>
                  <p className="font-semibold">{selectedProject.business_name}</p>
                </div>
                <Eye className="w-4 h-4 text-[#a0a0a0]" />
              </Link>

              {/* Description */}
              <div>
                <h4 className="font-semibold mb-2">Project Description</h4>
                <p className="text-[#0e0e0e] whitespace-pre-wrap">{selectedProject.description}</p>
              </div>

              {/* Deliverables */}
              {selectedProject.deliverables && (
                <div className="p-4 bg-[#f5ead0] rounded-[14px] border border-[#e8d49a]">
                  <h4 className="font-semibold text-[#8a6a1a] mb-2">Expected Deliverables</h4>
                  <p className="text-[#8a6a1a]">{selectedProject.deliverables}</p>
                </div>
              )}

              {/* Key Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                {selectedProject.location && (
                  <div className="p-4 bg-[#f7f5f2] rounded-[14px]">
                    <div className="flex items-center gap-2 text-[#5a5a5a] mb-1">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">Location</span>
                    </div>
                    <p className="font-medium">{selectedProject.location}</p>
                  </div>
                )}

                {selectedProject.event_date && (
                  <div className="p-4 bg-[#f5ead0] rounded-[14px]">
                    <div className="flex items-center gap-2 text-[#d4a843] mb-1">
                      <CalendarIcon className="w-4 h-4" />
                      <span className="text-sm">Shoot Date</span>
                    </div>
                    <p className="font-medium text-[#8a6a1a]">
                      {selectedProject.event_date} {selectedProject.event_time && `@ ${selectedProject.event_time}`}
                    </p>
                    {selectedProject.shoot_duration && (
                      <p className="text-sm text-[#d4a843]">{selectedProject.shoot_duration} hours</p>
                    )}
                  </div>
                )}

                <div className="p-4 bg-[#f7f5f2] rounded-[14px]">
                  <div className="flex items-center gap-2 text-[#5a5a5a] mb-1">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Application Deadline</span>
                  </div>
                  <p className="font-medium">{selectedProject.application_deadline || selectedProject.deadline}</p>
                </div>

                {selectedProject.deliverables_deadline && (
                  <div className="p-4 bg-[#f5ead0] rounded-[14px]">
                    <div className="flex items-center gap-2 text-[#d4a843] mb-1">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">Finals Due</span>
                    </div>
                    <p className="font-medium text-[#8a6a1a]">{selectedProject.deliverables_deadline}</p>
                  </div>
                )}
              </div>

              {/* Specialties */}
              {selectedProject.specialty_required?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Required Specialties</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.specialty_required.map((s, idx) => (
                      <span key={idx} className="px-3 py-1 rounded-full bg-[#0e0e0e] text-white text-sm">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Apply Button */}
              {user?.role === 'creative' && selectedProject.status === 'open' && (
                <Button
                  onClick={() => { setDetailsOpen(false); setApplyOpen(true); }}
                  className="w-full rounded-full bg-[#0e0e0e] text-white hover:bg-[#1a1a1a]"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Apply to This Project
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
